define(function (require) {
	//possible statuses
	//     public enum VirusScanStatus
	// {
	//     NotSupported,
	//     Completed,
	//     Pending,
	//     Failed
	// }

	const NOTSUPPORTED = 'NotSupported';
	const COMPLETED = 'Completed';
	const PENDING = 'Pending';
	const FAILED = 'Failed';
	const INFECTED = 'Infected';

	// Cached lowercase versions
	const NOTSUPPORTED_LOWER = NOTSUPPORTED.toLowerCase();
	const COMPLETED_LOWER = COMPLETED.toLowerCase();
	const PENDING_LOWER = PENDING.toLowerCase();
	const FAILED_LOWER = FAILED.toLowerCase();

	/********************************************************************* */
	/* 	VIRUS SCAN MONITORING SYSTEM
	/*
	/* 	Monitors uploaded attachments during virus scanning. When files are
	/* 	upload, they may have "Pending" status while being scanned. This
	/* 	utility polls the server until scanning completes.
	/*
	/* 	USAGE:
	/* 	  const result = await AttachmentUtility.monitorScanStatus(
	/* 	    attachmentsCollection, 'Use', 1016
	/* 	  );
	/* 	  if (result.noPendingFiles) {
	/* 	    // No files were pending
	/* 	  } else if (result.scanningCompleted) {
	/* 	    // Scanning finished, use result.attachments
	/* 	  }
	/*
	/* 	FLOW:
	/* 	  1. Check if any attachments are pending
	/* 	  2. If none pending: return { noPendingFiles: true }
	/* 	  3. If pending: fetch latest status from server
	/* 	  4. If still pending: poll every 3s until complete
	/* 	  5. Return { scanningCompleted: true, attachments: [...] }
	/*
	/********************************************************************* */

	// Monitoring configuration
	const POLL_INTERVAL_MS = 3000; // Wait 3 seconds between status checks
	const MAX_POLL_ATTEMPTS = 20; // Stop checking after 20 attempts (~60 seconds)
	const MAX_FETCH_FAILURES = 5; // Stop after 5 consecutive network failures

	// Singleton flag - only allow one monitoring session at a time
	let isMonitoring = false;

	/**
	 * Maps product family name to Hansen parent moniker
	 * @param {string} productFamily - e.g., "Use", "Building", "Planning"
	 * @returns {string} Full Hansen moniker or empty string if not found
	 */
	let _getParentMonikerFromProductFamily = (productFamily) => {
		switch (productFamily.toUpperCase()) {
			case "BUILDING":
				return "Hansen.CDR.Building.BuildingApplication";
			case "BUSINESSLICENSE":
				return "Hansen.CDR.BusinessLicense.LicenseApplication";
			case "CODEENFORCEMENT":
				return "Hansen.CDR.CodeEnforcement.Case";
			case "PLANNING":
				return "Hansen.CDR.Planning.PlanningApplication";
			case "PROJECT":
				return "Hansen.CDR.Project.ProjectApplication";
			case "TRADELICENSE":
				return "Hansen.CDR.TradeLicense.TradeLicense";
			case "USE":
				return "Hansen.CDR.Use.UseApplication";
			case "SERVICEREQUEST":
				return "Hansen.CRM.ServiceRequest";
			default:
				return "";
		}
	};

	/**
	 * Parses virus scan status from attachment and creates virusScanResults object
	 * @param {Object} attachment - Attachment with virusScanStatus and itemInfected properties
	 * @param {number} index - Index for debugging
	 * Sets attachment.virusScanResults with: notSupported, completed, pending, failed, infected, safe, status
	 */
	let _processScanStatus = (attachment,index) => {
		//bounce if we've been processed before.
		if (attachment.virusScanResults) return;

		let status = {//default to "safe" scan status as files that are not scanned will be treated as safe.
			notSupported: true,
			completed: true,
			pending: false,
			failed: false,
			infected: false,
			safe: true,
			status: COMPLETED
		};

		if (attachment.virusScanStatus != null && attachment.virusScanStatus.trim() != '') {
			let vStatus = attachment.virusScanStatus.toLowerCase();
			let isInfected = attachment.itemInfected === true;
			
			status = {
				notSupported: vStatus === NOTSUPPORTED_LOWER,
				completed: vStatus === COMPLETED_LOWER || vStatus === NOTSUPPORTED_LOWER,//we treat unsupported files as if they are completed
				pending: vStatus === PENDING_LOWER,
				failed: vStatus === FAILED_LOWER,
				infected: isInfected, // If itemInfected is true, always mark as infected regardless of scan status
				safe: vStatus !== PENDING_LOWER && !isInfected
			};
			
			// Determine final status string - infected takes highest priority
			if (status.infected) {
				status.status = INFECTED;
			} else if (status.pending) {
				status.status = PENDING;
			} else if (status.failed) {
				status.status = FAILED;
			} else {
				status.status = COMPLETED;
			}
		}
		attachment.virusScanResults = status;
	};

	/**
	 * Checks if any attachments are pending virus scan
	 * @param {Object|Array} attachments - Backbone collection, array, or single attachment
	 * @returns {boolean} True if any attachments have pending status
	 */
	let _shouldMonitorAttachments = (attachments) => {
		// No attachments provided
		if (!attachments) return false;
		
		let attachmentsArray = [];
		
		// Handle Backbone collection
		if (attachments.models) {
			attachmentsArray = attachments.models.map(model => model.toJSON());
		}
		// Handle array
		else if (Array.isArray(attachments)) {
			attachmentsArray = attachments;
		}
		// Handle single attachment
		else {
			attachmentsArray = [attachments];
		}
		
		// No attachments in array
		if (attachmentsArray.length === 0) return false;
		
		// Parse virus scan status for all attachments
		attachmentsArray.forEach((att, index) => {
			_processScanStatus(att, index);
		});
		
		// Check if any are pending
		return attachmentsArray.some(att => att.virusScanResults?.pending === true);
	};

	/**
	 * Fetches all attachments from server and checks for pending status
	 * @param {string} parentMoniker - Full Hansen moniker
	 * @param {number} parentPrimaryKey - Parent record ID
	 * @returns {Promise<{hasPending: boolean, attachments: Array}>}
	 */
	let _checkAllAttachmentsStatus = (parentMoniker, parentPrimaryKey) => {
		return new Promise((resolve, reject) => {
			// Use jQuery AJAX to fetch all attachments for this parent
			$.ajax({
				url: `/delegate/civics-api/api/core/attachments/${parentMoniker}/${parentPrimaryKey}/`,
				type: 'GET',
				success: (response) => {
					const attachments = response?.data || [];
					const hasPending = _shouldMonitorAttachments(attachments);
					resolve({ hasPending, attachments });
				},
				error: (xhr, status, error) => {
					reject({ error: error, status: status });
				}
			});
		});
	};

	/**
	 * Polls server repeatedly until no attachments are pending
	 * @param {string} parentMoniker - Full Hansen moniker
	 * @param {number} parentPrimaryKey - Parent record ID
	 * @returns {Promise<{hasPending: boolean, attachments: Array}>}
	 * Polls every 3s, max 20 attempts, max 5 consecutive failures
	 */
	let _pollAllAttachments = (parentMoniker, parentPrimaryKey) => {
		return new Promise((resolve, reject) => {
			let attemptCount = 0;
			let failureCount = 0;
			let intervalId = null;
			
			const checkStatus = () => {
				attemptCount++;
				
				_checkAllAttachmentsStatus(parentMoniker, parentPrimaryKey).then((result) => {
					failureCount = 0;
					
					if (!result.hasPending) {
						clearInterval(intervalId);
						resolve(result);
					} else if (attemptCount >= MAX_POLL_ATTEMPTS) {
						clearInterval(intervalId);
						reject(new Error(`Max poll attempts (${MAX_POLL_ATTEMPTS}) reached`));
					}
				}).catch((error) => {
					failureCount++;
					
					if (failureCount >= MAX_FETCH_FAILURES) {
						clearInterval(intervalId);
						reject(new Error(`Max fetch failures (${MAX_FETCH_FAILURES}) reached`));
					}
				});
			};
			
			intervalId = setInterval(checkStatus, POLL_INTERVAL_MS);
		});
	};

	/**
	 * Validates and resolves parent moniker from productFamily or full moniker
	 * @param {string} productFamilyOrMoniker - "Use" or "Hansen.CDR.Use.UseApplication"
	 * @param {number} parentPrimaryKey - Parent record ID
	 * @returns {string} Full Hansen moniker
	 * @throws {Error} If parameters are invalid
	 */
	let _resolveParentMoniker = (productFamilyOrMoniker, parentPrimaryKey) => {
		let parentMoniker = productFamilyOrMoniker;
		if (!productFamilyOrMoniker.includes('.')) {
			parentMoniker = _getParentMonikerFromProductFamily(productFamilyOrMoniker);
		}
		
		if (!parentMoniker || !parentPrimaryKey) {
			throw new Error('parentMoniker/productFamily and parentPrimaryKey are required');
		}
		
		return parentMoniker;
	};

	/**
	 * Main entry point: Monitors attachments until virus scanning completes
	 * @param {Object|Array} attachments - Backbone collection, array, or single attachment
	 * @param {string} productFamilyOrMoniker - "Use" or "Hansen.CDR.Use.UseApplication"
	 * @param {number} parentPrimaryKey - Parent record ID
	 * @param {Function} callback - Optional Node-style callback(error, result)
	 * @returns {Promise<Object>} { noPendingFiles: true } or { scanningCompleted: true, attachments: [...] }
	 * Singleton: Only one monitoring session allowed at a time
	 */
	let _monitorScanStatus = async (attachments, productFamilyOrMoniker, parentPrimaryKey, callback) => {
		try {
			if (!_shouldMonitorAttachments(attachments)) {
				const result = { noPendingFiles: true };
				if (callback) callback(null, result);
				return result;
			}
			
			if (isMonitoring) {
				throw new Error('Monitoring already in progress');
			}
			
			const parentMoniker = _resolveParentMoniker(productFamilyOrMoniker, parentPrimaryKey);
			
			isMonitoring = true;
			
			const result = await _checkAllAttachmentsStatus(parentMoniker, parentPrimaryKey);
			
			if (!result.hasPending) {
				isMonitoring = false;
				const finalResult = { scanningCompleted: true, attachments: result.attachments };
				if (callback) callback(null, finalResult);
				return finalResult;
			}
			
			const polledResult = await _pollAllAttachments(parentMoniker, parentPrimaryKey);
			isMonitoring = false;
			const finalResult = { scanningCompleted: true, attachments: polledResult.attachments };
			if (callback) callback(null, finalResult);
			return finalResult;
		} catch (error) {
			isMonitoring = false;
			if (callback) callback(error);
			throw error;
		}
	};

	/**
	 * Public wrapper: Parses virus scan status for attachments
	 * @param {Object|Array} attachment - Backbone collection, array, or single attachment
	 * Modifies attachments in place, adding virusScanResults property
	 */
	let _parseVirusScanStatus = (attachment) => {
		//bounce if we have no attachments
		if (!attachment) return;

		//if we get a Backbone collection, process each model
		if (attachment.models) {
			attachment.models.forEach((model) => {
				const json = model.toJSON();
				_processScanStatus(json);
				model.set('virusScanResults', json.virusScanResults, { silent: true });
			});
		}

		//if we get an array, process each individual attachment
		else if (Array.isArray(attachment)) {
			attachment.forEach((el,index) => {
				_processScanStatus(el,index);
			});
		} else {
			_processScanStatus(attachment);
		}
		//no return, we're editing the object directly.
	};

	return {
		NOTSUPPORTED,
		COMPLETED,
		PENDING,
		FAILED,
		INFECTED,
		monitorScanStatus: _monitorScanStatus,
		parseVirusScanStatus: _parseVirusScanStatus
	}
});